/**
 * SB6-II-01 — Inspection-instruction state chip, on D81's slot recipe.
 *
 * Per the locked colour matrix in
 * `SB6-II-01-inspection-instruction-page.md` §Table, and the slot each band
 * now means:
 *   white  ← CREATED, SCHEDULED             → neutral   (inert · not started)
 *   yellow ← ONSITE_EXECUTION, DATA_TRANSFER → attention (needs a human)
 *   blue   ← DATA_PROCESSING, ANALYSIS      → info      (running)
 *   green  ← COMPLETED                      → success
 *   red    ← WITHDRAWN, REJECTED            → critical  (terminal · blocking)
 *
 * The state → band matrix is UNCHANGED; only what a band resolves to moved.
 * Mirrors `../projects/ProjectStatusChip.tsx`, which now delegates to the same
 * shared `statusChipStyle` — there is no per-file copy of the fills left.
 *
 * `data-state` + `data-color` attributes are kept for Playwright
 * assertions (AC8).
 */
import { type ReactElement } from "react";
import { Chip } from "@mui/material";
import { statusChipStyle, type StatusSlot } from "@aeriosiq/ui";
import {
  INSPECTION_INSTRUCTION_STATES,
  type InspectionInstructionState,
} from "./types.js";

export type IiColorBand = "white" | "yellow" | "blue" | "green" | "red";

/** Band → D81 slot. The band is the domain vocabulary `data-color` publishes;
 *  the slot is the MEANING that carries the colour. */
const SLOT_BY_BAND: Record<IiColorBand, StatusSlot> = {
  white: "neutral",
  yellow: "attention",
  blue: "info",
  green: "success",
  red: "critical",
};

export const II_STATE_COLOR: Record<InspectionInstructionState, IiColorBand> = {
  CREATED: "white",
  SCHEDULED: "white",
  ONSITE_EXECUTION: "yellow",
  DATA_TRANSFER: "yellow",
  DATA_PROCESSING: "blue",
  ANALYSIS: "blue",
  COMPLETED: "green",
  WITHDRAWN: "red",
  REJECTED: "red",
};

export const II_STATE_LABEL: Record<InspectionInstructionState, string> = {
  CREATED: "Created",
  SCHEDULED: "Scheduled",
  ONSITE_EXECUTION: "Onsite execution",
  DATA_TRANSFER: "Data transfer",
  DATA_PROCESSING: "Data processing",
  ANALYSIS: "Analysis",
  COMPLETED: "Completed",
  WITHDRAWN: "Withdrawn",
  REJECTED: "Rejected",
};

/** Human label for a state — used by the States filter + chip. */
export function iiStateLabel(state: InspectionInstructionState): string {
  return II_STATE_LABEL[state];
}

/** Stable option list for the States multiselect ({id,name}). */
export const II_STATE_OPTIONS: readonly {
  id: InspectionInstructionState;
  name: string;
}[] = INSPECTION_INSTRUCTION_STATES.map((s) => ({
  id: s,
  name: iiStateLabel(s),
}));

export function InspectionInstructionStatusChip({
  state,
}: {
  state: InspectionInstructionState;
}): ReactElement {
  const color = II_STATE_COLOR[state];
  return (
    <Chip
      size="small"
      label={II_STATE_LABEL[state]}
      data-state={state}
      data-color={color}
      sx={(t) => {
        // The hue owns the fill and the keyline; the label stays neutral. Every
        // band now carries a keyline, not just the old near-transparent white.
        const chip = statusChipStyle(t, SLOT_BY_BAND[color]);
        return {
          backgroundColor: chip.backgroundColor,
          color: chip.color,
        };
      }}
    />
  );
}
